import { ref } from "vue";
import { defineStore } from "pinia";
import ApiService from "@/core/services/ApiService";
import JwtService from "@/core/services/JwtService";
import Swal from "sweetalert2";
import type { IUser } from "@/core/types/user";

// Use shared IUser as the base for authenticated user; attach access_token in store state
export type AuthUser = IUser & {
  access_token?: string;
};

export interface LoginCredentials {
  username?: string;
  email?: string;
  password: string;
}

export type RegisterPayload = Partial<IUser> & { password: string };

export const SYSTEM_REFRESH_TOKEN_KEY = "system_refresh_token";

export const useAuthStore = defineStore("auth", () => {
  const errors = ref({});
  const user = ref<AuthUser>({} as AuthUser);
  const isAuthenticated = ref(!!JwtService.getToken());
  const sessionTimeoutId = ref<NodeJS.Timeout | null>(null);
  const warningTimeoutId = ref<NodeJS.Timeout | null>(null);

  // Session timeout management
  async function initializeSessionTimeout() {
    // Skip session timeout in development mode
    if (import.meta.env.DEV) {
      console.log('Session timeout: Disabled in development mode');
      return;
    }

    // Clear existing timeouts
    clearSessionTimeouts();

    if (!isAuthenticated.value) {
      console.log('Session timeout: User not authenticated, skipping');
      return;
    }
  }

  function clearSessionTimeouts() {
    if (sessionTimeoutId.value) {
      clearTimeout(sessionTimeoutId.value);
      sessionTimeoutId.value = null;
    }
    if (warningTimeoutId.value) {
      clearTimeout(warningTimeoutId.value);
      warningTimeoutId.value = null;
    }
  }

  function showLogoutWarning() {
    Swal.fire({
      title: 'Session Expiring Soon',
      text: 'Your session will expire in 5 minutes. Do you want to extend your session?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Extend Session',
      cancelButtonText: 'Logout Now',
      allowOutsideClick: false,
      allowEscapeKey: false,
    }).then((result) => {
      if (result.isConfirmed) {
        // Extend session by re-initializing timeout
        initializeSessionTimeout();
      } else {
        // User chose to logout
        logout();
      }
    });
  }

  function handleSessionTimeout() {
    // Save current URL before logout
    const currentPath = window.location.pathname + window.location.search + window.location.hash;
    if (currentPath && currentPath !== '/sign-in' && currentPath !== '/logon' && currentPath !== '/oauth/callback') {
      sessionStorage.setItem('redirect_after_login', currentPath);
    }

    Swal.fire({
      title: 'Session Expired',
      text: 'Your session has expired. Please login again.',
      icon: 'info',
      confirmButtonText: 'OK',
      allowOutsideClick: false,
      allowEscapeKey: false,
    }).then(() => {
      logout();
    });
  }

  function setAuth(authData: { user: IUser; access_token: string, refresh_token?: string }) {
    isAuthenticated.value = true;
    user.value = { ...(authData.user as any), access_token: authData.access_token } as AuthUser;
    console.log("setAuth", user.value);
    errors.value = {};
    JwtService.saveToken(authData.access_token);
    if (authData.refresh_token) {
      localStorage.setItem(SYSTEM_REFRESH_TOKEN_KEY, authData.refresh_token);
    }

    // Initialize session timeout after successful authentication
    initializeSessionTimeout();
  }

  function setError(error: any) {
    errors.value = { ...error };
  }

  function purgeAuth() {
    isAuthenticated.value = false;
    user.value = {} as AuthUser;
    errors.value = [];
    JwtService.destroyToken();
    localStorage.removeItem(SYSTEM_REFRESH_TOKEN_KEY);
  }

  function login(credentials: LoginCredentials) {
    return ApiService.post("/api/v1/auth/login", credentials)
      .then(async ({ data }) => {
        console.log(data);
        if (data.status === 'OK') {
          // Save access token first, then populate user via /auth/me
          try {
            if (data?.data?.access_token) {
              JwtService.saveToken(data.data.access_token);
            }
            await verifyAuth();
          } catch (e) {
            // Fallback to payload if verifyAuth fails
            setAuth(data.data);
          }
        } else if (data.status === 'ERROR') {
          setError(data.data);
        } else {
          setError({ message: 'Unknown response format' });
        }
      })
      .catch(({ response }) => {
        console.log(response);
        const error_message = response.data.errors || response.data;
        setError(error_message);
      });
  }

  function getErrorMessage(errors, key = "message", defaultKey = "error") {
    if (!errors || typeof errors !== "object") return "Unknown error";
    // 1. ถ้ามี key ที่ต้องการ
    if (errors[key]) return errors[key];
    // 2. fallback เป็น key อื่น (เช่น "error")
    if (defaultKey && errors[defaultKey]) return errors[defaultKey];
    // 3. fallback เป็นค่าตัวแรกใน object
    const values = Object.values(errors);
    if (values.length) return values[0];
    // 4. fallback default
    return "Unknown error";
  }

  function logout() {
    return ApiService.post("/api/v1/auth/logout")
      .catch((error) => {
        console.warn('Logout API call failed, but clearing local state:', error);
      })
      .finally(() => {
        clearSessionTimeouts();
        purgeAuth();
      });
  }

  function register(credentials: RegisterPayload) {
    return ApiService.post("/api/v1/auth/register", credentials)
      .then(async ({ data }) => {
        if (data.status === 'OK') {
          try {
            if (data?.data?.access_token) {
              JwtService.saveToken(data.data.access_token);
            }
            await verifyAuth();
          } catch (e) {
            setAuth(data.data);
          }
        } else if (data.status === 'ERROR') {
          setError(data.data);
        } else {
          setError({ message: 'Unknown response format' });
        }
      })
      .catch(({ response }) => {
        setError(response.data.errors);
      });
  }

  function forgotPassword(email: string) {
    return ApiService.post("/api/v1/auth/forgot_password", email)
      .then(() => {
        setError({});
      })
      .catch(({ response }) => {
        setError(response.data.errors);
      });
  }

  function verifyAuth() {
    return new Promise<void>((resolve, reject) => {
      if (JwtService.getToken()) {
        ApiService.setHeader();
        // const access_token = JwtService.getToken();
        // console.log("verifyAuth", access_token);
        ApiService.get("/api/v1/auth/me")
          .then(({ data }) => {
            if (data.status === 'OK') {
              setAuth(data.data);
              resolve();
            } else if (data.status === 'ERROR') {
              setError(data.data);
              reject(new Error(data.data.message || 'Verification failed'));
            } else {
              setError({ message: 'Unknown response format' });
              reject(new Error('Unknown response format'));
            }
          })
          .catch(({ response }) => {
            console.error('Auth verification error:', response?.data);
            setError(response?.data?.errors || { message: 'Network error' });
            reject(new Error('Auth verification failed'));
          });
      } else {
        reject(new Error('No token found'));
      }
    });
  }

  function getUser() {
    return user.value;
  }

  return {
    errors,
    user,
    isAuthenticated,
    login,
    logout,
    // register,
    // forgotPassword,
    verifyAuth,
    getErrorMessage,
    getUser,
    initializeSessionTimeout,
    clearSessionTimeouts,
    setAuth,
    purgeAuth,
  };
});
